import * as axios from 'axios';
import { AxiosRequestConfig } from 'axios';
import { TokenService, History } from './../services';
import { store } from '$/store';
import { logout } from '$/store/login/actions';
import * as Bixby from '$/core';
import { OutgoingHttpHeaders } from 'http';

interface RequestConfig extends AxiosRequestConfig {
  sendAuthentication?: boolean;
  authTokenOverride?: string;
  globalErrorHandling?: boolean;
}

class ServerRoute {
  private constructor(controller: string) {
    this.controller = controller;

    if (!this.controller) {
      throw new Error(`Expected controller argument to be defined.`);
    }
  }

  public static forAction(controller: string, action?: string): ServerRoute {
    const result: ServerRoute = new ServerRoute(controller);
    const actionUrl = Bixby.Utils.isDefined(action) ? `/${action}` : '';
    result.fullUrl = `/${controller}${actionUrl}`;
    return result;
  }

  private readonly controller: string;
  private fullUrl?: string;

  public get url(): string {
    return this.fullUrl || '';
  }
}

const instance = axios.default;
instance.interceptors.request.use((config: RequestConfig) => {
  const access_token: string = TokenService.getAccessToken() || '';
  const defaultHeaders: OutgoingHttpHeaders = {
    'Content-Type': 'application/json',
  };
  if (config.authTokenOverride) {
    defaultHeaders.Authorization = `Bearer ${config.authTokenOverride}`;
  } else if (access_token && config.sendAuthentication !== false) {
    defaultHeaders.Authorization = `Bearer ${access_token}`;
  }

  return Object.assign({}, config, {
    headers: Object.assign({}, defaultHeaders, config.headers || {}),
    
  });
});

instance.interceptors.response.use(undefined, async (result: any) => {
  const { response, config } = result;

  if (config && config.globalErrorHandling === false) {
    throw result;
  }

  if (response && response.status === 401) {
    if (History.location.pathname !== '/login' && !store.getState().loginState.isRefreshModalOpen) {
      await store.dispatch(logout() as any);
    }
  }

  if (response && response.status === 403) {
    if (History.location.pathname !== '/login' && !store.getState().loginState.isRefreshModalOpen) {
      const errorCode: string = (result as any).response.data.error as string;
      if (errorCode === '403.6') {
        await store.dispatch(logout(new Error('Invalid IP address')) as any);
      }
    }
  }

  throw result;
});

interface RequestConfig extends AxiosRequestConfig {
  headers?: any;
  sendAuthentication?: boolean;
  authTokenOverride?: string;
  globalErrorHandling?: boolean;
}

async function get<TResult>(routeData: ServerRoute, queryString?: string, config?: RequestConfig): Promise<TResult> {
  return (await instance.get(makeUrl(routeData, queryString), config)).data;
}

async function getFile(routeData: ServerRoute, queryString?: string, configOrUndefined?: RequestConfig): Promise<Blob> {
  const config = Object.assign({ responseType: 'blob' }, configOrUndefined || {});
  const response = await instance.get(makeUrl(routeData, queryString), config);
  const blob = response.data as Blob;

  return blob;
}

async function getFileWithMetadata(
  routeData: ServerRoute,
  queryString?: string,
  configOrUndefined?: RequestConfig
): Promise<Bixby.Models.FileResultMetadata> {
  const config = Object.assign({ responseType: 'blob' }, configOrUndefined || {});
  const response = await instance.get(makeUrl(routeData, queryString), config);
  const blob = response.data as Blob;
  const contentDisposition: string = response.headers['content-disposition'];

  // https://stackoverflow.com/a/23054920
  // const match = contentDisposition && contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const match = contentDisposition && contentDisposition.match(/filename\*?=['"]?(?:;UTF-\d['"]*)?([^;\r\n"]*)['"]?;?/);

  const filename = match ? match[1] : undefined;
  return { blob, filename };
}

async function postFileWithMetadata(
  routeData: ServerRoute,
  payload: any,
  configOrUndefined?: RequestConfig
): Promise<Bixby.Models.FileResultMetadata> {
  const config = Object.assign({ responseType: 'blob' }, configOrUndefined || {});
  const response = await instance.post(makeUrl(routeData), payload, config);
  const blob = response.data as Blob;
  const contentDisposition: string = response.headers['content-disposition'];

  // https://stackoverflow.com/a/23054920
  const match = contentDisposition && contentDisposition.match(/filename\*?=["]?(?:UTF-\d["]*)?([^\r\n"]*)['"]?;?/);

  const filename = match ? match[1] : undefined;
  return { blob, filename };
}

async function getAnonymous<TResult>(
  routeData: ServerRoute,
  queryString?: string,
  config?: RequestConfig
): Promise<TResult> {
  return (
    await instance.get(
      makeUrl(routeData, queryString),
      Object.assign({}, config || {}, {
        sendAuthentication: false,
      }) as any
    )
  ).data;
}

async function post<TResult>(routeData: ServerRoute, payload: any, config?: RequestConfig): Promise<TResult> {
  return (await instance.post(makeUrl(routeData), payload, config)).data;
}

async function postJSON<TResult>(routeData: ServerRoute, payload: any): Promise<TResult> {
  const jsonPostConfig = {
    headers: {
      'Content-Type': undefined,

    },
  } as RequestConfig;
  return (await instance.post(makeUrl(routeData), payload, jsonPostConfig)).data;
}

async function postLargeJSON<TResult>(routeData: ServerRoute, payload: any): Promise<TResult> {
  const jsonPostConfig = {
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    headers: {
      'Content-Type': 'multipart/form-data',

    },
  } as RequestConfig;
  return (await instance.post(makeUrl(routeData), payload, jsonPostConfig)).data;
}

async function postAnonymous<TResult>(routeData: ServerRoute, payload: any, config?: RequestConfig): Promise<TResult> {
  return (
    await instance.post(
      makeUrl(routeData),
      payload,
      Object.assign({}, config || {}, {
        sendAuthentication: false,
      }) as any
    )
  ).data;
}

async function patch<TResult>(routeData: ServerRoute, payload: any, config?: RequestConfig): Promise<TResult> {
  return (await instance.patch(makeUrl(routeData), payload, config)).data;
}

async function patchJSON<TResult>(routeData: ServerRoute, payload: any): Promise<TResult> {
  const jsonPatchConfig = {
    headers: {
      'Content-Type': 'application/json-patch+json',
    },
  } as RequestConfig;
  return (await instance.patch(makeUrl(routeData), payload, jsonPatchConfig)).data;
}

async function put<TResult>(routeData: ServerRoute, payload: any, config?: RequestConfig): Promise<TResult> {
  return (await instance.put(makeUrl(routeData), payload, config)).data;
}

async function apiDelete<TResult>(routeData: ServerRoute, config?: RequestConfig): Promise<TResult> {
  return (await instance.delete(makeUrl(routeData), config)).data;
}

function getErrorMessage(e: Bixby.Models.BixbyError) {
  if (
    e &&
    e.response &&
    (e.response.status === 400 || e.response.status === 404 || e.response.status === 409) &&
    e.response.message
  ) {
    if (typeof e.response.message === 'string') {
      return e.response.message;
    } else if (typeof e.response.title === 'string') {
      return e.response.title;
    }
  }
  return Bixby.Utils.isDefined(e) ? e.message : '';
}

function getStatus(e: Bixby.Models.BixbyError) {
  if (e && e.response && e.response.status) {
    return e.response.status;
  }
  return undefined;
}

function makeUrl(routeData: ServerRoute, queryString?: string | null) {
  const baseUrl: string = Bixby.Configuration.api;
  let url = `${baseUrl}/api${routeData.url}`;

  if (queryString && queryString.length > 0) {
    url += `?${queryString}`;
  }

  return url;
}

export default {
  ServerRoute,
  get,
  getFile,
  getFileWithMetadata,
  getAnonymous,
  post,
  postJSON,
  postLargeJSON,
  postAnonymous,
  patch,
  patchJSON,
  put,
  delete: apiDelete,
  axios: instance,
  getErrorMessage,
  getStatus,
  postFileWithMetadata,
};
